/**
 * User Configuration Service
 * Business logic for user configuration operations
 */

import { 
  API_ENDPOINTS, 
  makeAuthenticatedApiRequest, 
  type ApiResult 
} from './apiConfig';
import { validateAccessToken } from './validators';

/**
 * User feature settings interface
 */
export interface UserFeatureSettings {
  keywordLearningEnabled?: boolean;
  aiCategorizationEnabled?: boolean;
}

/**
 * User configuration item from API
 */
export interface UserConfigItem {
  PK: string;
  SK: string;
  isConfigured: boolean;
  showOnboarding: boolean;
  showNaturalLanguageSetup: boolean;
  savingsGoal: number;
  budgetStartDate?: string; // ISO 8601 date format (YYYY-MM-DD)
  features: UserFeatureSettings;
  createdAt: string;
  updatedAt: string;
}

/**
 * Update user configuration request
 */
export interface UpdateUserConfigRequest {
  isConfigured?: boolean;
  showOnboarding?: boolean;
  showNaturalLanguageSetup?: boolean;
  savingsGoal?: number;
  budgetStartDate?: string; // ISO 8601 date format (YYYY-MM-DD)
  features?: UserFeatureSettings;
}

/**
 * Update user configuration response
 */
export interface UpdateUserConfigResponse {
  message: string;
  config: UserConfigItem;
}

/**
 * Get user configuration response
 */
export interface GetUserConfigResponse {
  config: UserConfigItem;
}

/**
 * Update user configuration via API
 * All fields are optional - only provided fields are updated
 */
export async function updateUserConfig(
  configUpdate: UpdateUserConfigRequest,
  accessToken: string
): Promise<ApiResult<UpdateUserConfigResponse>> {
  // Input validation
  validateAccessToken(accessToken);
  
  // Validate config update object
  if (!configUpdate || typeof configUpdate !== 'object') {
    throw new Error('Config update must be a valid object');
  }
  
  // Validate optional fields
  if (configUpdate.isConfigured !== undefined && typeof configUpdate.isConfigured !== 'boolean') {
    throw new Error('isConfigured must be a boolean');
  }
  
  if (configUpdate.showOnboarding !== undefined && typeof configUpdate.showOnboarding !== 'boolean') {
    throw new Error('showOnboarding must be a boolean');
  }
  
  if (configUpdate.showNaturalLanguageSetup !== undefined && typeof configUpdate.showNaturalLanguageSetup !== 'boolean') {
    throw new Error('showNaturalLanguageSetup must be a boolean');
  }
  
  if (configUpdate.savingsGoal !== undefined) {
    if (typeof configUpdate.savingsGoal !== 'number' || configUpdate.savingsGoal < 0 || configUpdate.savingsGoal > 999999999.99) {
      throw new Error('savingsGoal must be a number between 0 and 999999999.99');
    }
  }
  
  if (configUpdate.budgetStartDate !== undefined && configUpdate.budgetStartDate !== null) {
    // Validate ISO 8601 format (full timestamp: YYYY-MM-DDTHH:mm:ss.sssZ)
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
    if (typeof configUpdate.budgetStartDate !== 'string' || !isoDateRegex.test(configUpdate.budgetStartDate)) {
      throw new Error('budgetStartDate must be a valid ISO 8601 timestamp string (YYYY-MM-DDTHH:mm:ss.sssZ) or null');
    }
    
    // Validate it's a real date
    const date = new Date(configUpdate.budgetStartDate);
    if (isNaN(date.getTime())) {
      throw new Error('budgetStartDate must be a valid date');
    }
  }
  
  if (configUpdate.features !== undefined) {
    if (typeof configUpdate.features !== 'object' || configUpdate.features === null) {
      throw new Error('features must be a valid object');
    }
    
    if (configUpdate.features.keywordLearningEnabled !== undefined && typeof configUpdate.features.keywordLearningEnabled !== 'boolean') {
      throw new Error('features.keywordLearningEnabled must be a boolean');
    }
    
    if (configUpdate.features.aiCategorizationEnabled !== undefined && typeof configUpdate.features.aiCategorizationEnabled !== 'boolean') {
      throw new Error('features.aiCategorizationEnabled must be a boolean');
    }
  }
  
  const payload: UpdateUserConfigRequest = { ...configUpdate };
  
  return makeAuthenticatedApiRequest(API_ENDPOINTS.CONFIG.UPDATE, payload, accessToken);
}

/**
 * Get user configuration via API
 * Retrieves current user configuration settings
 */
export async function getUserConfig(
  accessToken: string
): Promise<ApiResult<GetUserConfigResponse>> {
  // Input validation
  validateAccessToken(accessToken);
  
  return makeAuthenticatedApiRequest(API_ENDPOINTS.CONFIG.GET, undefined, accessToken);
}